import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Card,
  TableCell,
  Checkbox,
  Avatar,
  IconButton,
  Button,
  Chip,
} from "@material-ui/core";
import {
  MoreVert as MoreVertIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Inventory2 as InventoryIcon,
  Business as BusinessIcon,
} from "@material-ui/icons";
import moment from "moment";
import DataTable from "../../../../components/DataTable";
import TicketFilters from "../../components/ticket-filters";
import TicketStatusChip from "../../components/ticket-status-chip";
import SlaIndicator from "../../components/sla-indicator";

const PRIORITY_CONFIG = {
  critical: { label: "Critical", bgColor: "rgba(211,47,47,0.1)", color: "#C62828" },
  high: { label: "High", bgColor: "rgba(245,124,0,0.1)", color: "#E65100" },
  medium: { label: "Medium", bgColor: "rgba(90,149,89,0.1)", color: "#4A8449" },
  low: { label: "Low", bgColor: "rgba(56,142,60,0.1)", color: "#2E7D32" },
};

const MODULE_LABELS = {
  stock_management: "Stock Mgmt",
  qr_serial: "QR Code",
  campaigns: "Campaign",
  dealers: "Dealer",
  reports: "Reports",
  account_billing: "Account",
  other: "Other",
};

const TicketListPage = ({
  tickets,
  totalTickets,
  page,
  rowsPerPage,
  isLoading,
  selectedIds,
  filterStatus,
  filterPriority,
  filterModule,
  filterAssignee,
  agents,
  onPageClick,
  onItemPerPageChange,
  onTicketClick,
  onToggleSelect,
  onSelectAll,
  onStatusChange,
  onPriorityChange,
  onModuleChange,
  onAssigneeChange,
  onClearFilters,
  onBulkAssign,
  canCreate,
  onCreateTicket,
  unreadByTicket = {},
}) => {
  const allSelected = tickets.length > 0 && selectedIds.length === tickets.length;

  return (
    <>
      <Helmet>
        <title>Support Tickets | Kood Asia Management Portal</title>
      </Helmet>
      <Box sx={{ backgroundColor: "background.default", minHeight: "100%", py: 3 }}>
        <Container maxWidth={false}>
          <Card sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 2 }}>
              <Typography variant="h4">
                Support Tickets
              </Typography>
              {canCreate && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PersonAddIcon style={{ fontSize: 18 }} />}
                  onClick={onCreateTicket}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#5A9559",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: 13,
                    px: 2,
                    "&:hover": { backgroundColor: "#4A8449" },
                  }}
                >
                  New Ticket
                </Button>
              )}
            </Box>
          </Card>

          {/* Stats Cards */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", mb: 3 }}>
            {[
              { label: "Open", count: tickets.filter((t) => t.status === "open").length, bgColor: "rgba(90,149,89,0.1)", color: "#5A9559" },
              { label: "Acknowledged", count: tickets.filter((t) => t.status === "acknowledged").length, bgColor: "rgba(230,81,0,0.1)", color: "#E65100" },
              { label: "In Progress", count: tickets.filter((t) => t.status === "in_progress").length, bgColor: "rgba(245,124,0,0.1)", color: "#F57C00" },
              { label: "Breached SLA", count: tickets.filter((t) => (t.sla_acknowledgement_due && new Date(t.sla_acknowledgement_due) < new Date() && !t.acknowledged_at) || (t.sla_response_due && new Date(t.sla_response_due) < new Date() && !t.first_response_at)).length, bgColor: "rgba(211,47,47,0.1)", color: "#D32F2F" },
            ].map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  padding: "20px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  display: "flex",
                  gap: "16px",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: stat.bgColor,
                  }}
                >
                  <InventoryIcon style={{ fontSize: 24, color: stat.color }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 13, color: "#6b778c", mb: 0.5 }}>{stat.label}</Typography>
                  <Typography sx={{ fontSize: 28, fontWeight: 600, color: "#172b4d" }}>{stat.count}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Filters */}
          <TicketFilters
            status={filterStatus}
            priority={filterPriority}
            smartkoodModule={filterModule}
            assignee={filterAssignee}
            agents={agents}
            onStatusChange={onStatusChange}
            onPriorityChange={onPriorityChange}
            onModuleChange={onModuleChange}
            onAssigneeChange={onAssigneeChange}
            onClearAll={onClearFilters}
          />

          {/* Bulk Actions Bar */}
          {selectedIds.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                backgroundColor: "#fff",
                borderRadius: "12px 12px 0 0",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Typography sx={{ fontSize: 14, color: "#6b778c" }}>
                  {selectedIds.length} selected
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PersonAddIcon style={{ fontSize: 16 }} />}
                  onClick={onBulkAssign}
                  sx={{ textTransform: "none", fontSize: 13 }}
                >
                  Assign
                </Button>
              </Box>
              <Typography sx={{ fontSize: 13, color: "#6b778c" }}>
                {totalTickets} tickets
              </Typography>
            </Box>
          )}

          {/* Ticket Table */}
          <DataTable
            isLoading={isLoading}
            rows={tickets}
            rowsPerPage={rowsPerPage}
            total={totalTickets}
            page={page}
            renderHeader={() => (
              <>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    onChange={onSelectAll}
                    sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#6b778c", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Ticket ID
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#6b778c", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Subject
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#6b778c", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Requester
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#6b778c", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Category
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#6b778c", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Priority
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#6b778c", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#6b778c", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Assigned To
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#6b778c", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  SLA
                </TableCell>
                <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#6b778c", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Created
                </TableCell>
              </>
            )}
            renderRow={(row) => {
              const priority = row.priority ? PRIORITY_CONFIG[row.priority] : null;
              const isSelected = selectedIds.includes(row.id);
              const unreadCount = unreadByTicket[row.id] || 0;
              const isUnread = unreadCount > 0;
              return (
                <>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => onToggleSelect(row.id)}
                      sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography
                        sx={{
                          fontWeight: isUnread ? 700 : 500,
                          color: "#5A9559",
                          cursor: "pointer",
                          fontSize: 14,
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={() => onTicketClick(row.id)}
                      >
                        {row.ticket_number}
                      </Typography>
                      {isUnread && (
                        <Box
                          aria-label={`${unreadCount} new message${unreadCount > 1 ? "s" : ""}`}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 20,
                            height: 18,
                            px: "6px",
                            borderRadius: "9px",
                            backgroundColor: "#6AAF68",
                            color: "#fff",
                            fontSize: "11px",
                            fontWeight: 700,
                            lineHeight: 1,
                            letterSpacing: "0.02em",
                          }}
                        >
                          {unreadCount}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#172b4d",
                        maxWidth: 260,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {row.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          fontSize: 11,
                          backgroundColor: "#f4f6f8",
                          color: "#6b778c",
                        }}
                      >
                        {row.reporter_name
                          ? row.reporter_name.split(" ").map((n) => n[0]).join("")
                            .toUpperCase()
                            .slice(0, 2)
                          : "?"}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {row.reporter_name}
                        </Typography>
                        {row.reporter_company_name && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              mt: "2px",
                              minWidth: 0,
                            }}
                          >
                            <BusinessIcon sx={{ fontSize: 12, color: "#6b778c", flexShrink: 0 }} />
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: "#6b778c",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={row.reporter_company_name}
                            >
                              {row.reporter_company_name}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={MODULE_LABELS[row.smartkood_module] || row.smartkood_module}
                      size="small"
                      sx={{
                        backgroundColor: "#f4f6f8",
                        color: "#6b778c",
                        fontSize: 12,
                        borderRadius: "4px",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {priority ? (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "4px 10px",
                          borderRadius: "12px",
                          backgroundColor: priority.bgColor,
                          color: priority.color,
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor: priority.color,
                          }}
                        />
                        {priority.label}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: 12, fontStyle: "italic", color: "#6b778c" }}>
                        Pending
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <TicketStatusChip status={row.status} />
                  </TableCell>
                  <TableCell>
                    {row.assignee_name ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: 11,
                            fontWeight: 500,
                            backgroundColor: "#5A9559",
                            color: "#fff",
                          }}
                        >
                          {row.assignee_name.split(" ").map((n) => n[0]).join("")
                            .toUpperCase()
                            .slice(0, 2)}
                        </Avatar>
                        <Typography sx={{ fontSize: 13 }}>{row.assignee_name}</Typography>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: 13, fontStyle: "italic", color: "#6b778c" }}>
                        Unassigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <SlaIndicator
                      dueDate={row.sla_response_due}
                      respondedAt={row.first_response_at}
                      compact
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 13, color: "#6b778c" }}>
                      {moment(row.created_at).format("MMM D, YYYY")}
                    </Typography>
                  </TableCell>
                </>
              );
            }}
            onPageClick={onPageClick}
            handleItemPerPageChange={onItemPerPageChange}
          />
        </Container>
      </Box>
    </>
  );
};

TicketListPage.propTypes = {
  tickets: PropTypes.array.isRequired,
  totalTickets: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  isLoading: PropTypes.bool,
  selectedIds: PropTypes.array,
  filterStatus: PropTypes.string,
  filterPriority: PropTypes.string,
  filterModule: PropTypes.string,
  filterAssignee: PropTypes.string,
  agents: PropTypes.array,
  onPageClick: PropTypes.func.isRequired,
  onItemPerPageChange: PropTypes.func.isRequired,
  onTicketClick: PropTypes.func.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onPriorityChange: PropTypes.func.isRequired,
  onModuleChange: PropTypes.func.isRequired,
  onAssigneeChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  onBulkAssign: PropTypes.func.isRequired,
  onCreateTicket: PropTypes.func,
};

export default TicketListPage;
